/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Plus, Trash2, Edit2, Receipt, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/accounting`;
const fmt = (n) => (Number(n) || 0).toLocaleString('az-AZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₼';
const STATUS_COLOR = {
  unpaid: 'bg-red-50 text-red-700 border-red-200',
  partial: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};
const STATUS_LABEL = { unpaid: 'Ödənməyib', partial: 'Qismən', paid: 'Ödənilib' };

export default function InvoicesTab() {
  const [subTab, setSubTab] = useState('invoices'); // invoices | vendors
  const [vendors, setVendors] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const [vendorModalOpen, setVendorModalOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [payInvoice, setPayInvoice] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [v, i] = await Promise.all([
        axios.get(`${API}/vendors`, { headers }),
        axios.get(`${API}/invoices`, { headers }),
      ]);
      setVendors(v.data || []);
      setInvoices(i.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const deleteVendor = async (id) => {
    if (!window.confirm('Tədarükçü deaktiv edilsin?')) return;
    try { await axios.delete(`${API}/vendors/${id}`, { headers }); toast.success('Deaktiv'); fetchAll(); }
    catch (e) { toast.error('Xəta'); }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm('Qaimə silinsin? (bütün ödəniş tarixçəsi də silinəcək)')) return;
    try { await axios.delete(`${API}/invoices/${id}`, { headers }); toast.success('Silindi'); fetchAll(); }
    catch (e) { toast.error('Xəta'); }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Yüklənir...</div>;

  const totalRemaining = invoices.reduce((s, i) => s + i.remaining, 0);
  const totalUnpaid = invoices.filter(i => i.status !== 'paid').length;

  return (
    <div className="space-y-4" data-testid="invoices-tab">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-xs text-red-700">Cəm qalıq borc</p>
          <p className="text-2xl font-bold text-red-800">{fmt(totalRemaining)}</p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <p className="text-xs text-yellow-700">Ödənməyən qaimələr</p>
          <p className="text-2xl font-bold text-yellow-800">{totalUnpaid}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <p className="text-xs text-emerald-700">Aktiv tədarükçülər</p>
          <p className="text-2xl font-bold text-emerald-800">{vendors.length}</p>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b pb-2">
        <Button variant={subTab === 'invoices' ? 'default' : 'outline'} onClick={() => setSubTab('invoices')} data-testid="inv-subtab-invoices">
          <Receipt className="w-4 h-4 mr-1" /> Qaimələr
        </Button>
        <Button variant={subTab === 'vendors' ? 'default' : 'outline'} onClick={() => setSubTab('vendors')} data-testid="inv-subtab-vendors">
          <Users className="w-4 h-4 mr-1" /> Tədarükçülər
        </Button>
      </div>

      {subTab === 'invoices' && (
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2"><FileText className="w-4 h-4" /> Qaimələr ({invoices.length})</h3>
            <Button onClick={() => setInvoiceModalOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-invoice-btn">
              <Plus className="w-4 h-4 mr-1" /> Yeni qaimə
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2">Tarix</th>
                  <th className="text-left px-3 py-2">Tədarükçü</th>
                  <th className="text-left px-3 py-2">Qaimə №</th>
                  <th className="text-center px-3 py-2">ƏDV?</th>
                  <th className="text-right px-3 py-2">Əsas</th>
                  <th className="text-right px-3 py-2">ƏDV</th>
                  <th className="text-right px-3 py-2">Cəm</th>
                  <th className="text-right px-3 py-2">Ödənilib</th>
                  <th className="text-right px-3 py-2">Qalıq</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 && (
                  <tr><td colSpan="11" className="text-center py-6 text-gray-400">Hələ qaimə yoxdur</td></tr>
                )}
                {invoices.map(i => (
                  <tr key={i.id} className="border-t hover:bg-gray-50" data-testid={`invoice-row-${i.id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{i.date}</td>
                    <td className="px-3 py-2">{i.vendor_name}</td>
                    <td className="px-3 py-2 font-mono text-xs">{i.invoice_number}</td>
                    <td className="px-3 py-2 text-center">{i.vendor_vat_payer ? '✅' : '❌'}</td>
                    <td className="px-3 py-2 text-right">{fmt(i.base_amount)}</td>
                    <td className="px-3 py-2 text-right text-purple-700">{fmt(i.vat_amount)}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmt(i.total)}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">{fmt(i.paid_so_far)}</td>
                    <td className="px-3 py-2 text-right font-bold text-red-700">{fmt(i.remaining)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs border px-2 py-0.5 rounded ${STATUS_COLOR[i.status]}`}>
                        {STATUS_LABEL[i.status]}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                      {i.status !== 'paid' && (
                        <Button size="sm" onClick={() => { setPayInvoice(i); setPaymentModalOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`pay-invoice-${i.id}`}>
                          💳 Ödə
                        </Button>
                      )}
                      <button onClick={() => deleteInvoice(i.id)} className="text-red-500">
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {subTab === 'vendors' && (
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <h3 className="font-semibold">Tədarükçülər ({vendors.length})</h3>
            <Button onClick={() => { setEditingVendor(null); setVendorModalOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-vendor-btn">
              <Plus className="w-4 h-4 mr-1" /> Yeni tədarükçü
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2">Ad</th>
                  <th className="text-center px-3 py-2">ƏDV ödəyici</th>
                  <th className="text-left px-3 py-2">Telefon</th>
                  <th className="text-left px-3 py-2">Qeyd</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {vendors.length === 0 && (
                  <tr><td colSpan="5" className="text-center py-6 text-gray-400">Hələ tədarükçü yoxdur</td></tr>
                )}
                {vendors.map(v => (
                  <tr key={v.id} className="border-t hover:bg-gray-50" data-testid={`vendor-row-${v.id}`}>
                    <td className="px-3 py-2 font-medium">{v.name}</td>
                    <td className="px-3 py-2 text-center">{v.vat_payer ? '✅' : '❌'}</td>
                    <td className="px-3 py-2 text-xs">{v.contact_phone || '-'}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{v.note || '-'}</td>
                    <td className="px-3 py-2 text-right space-x-1">
                      <button onClick={() => { setEditingVendor(v); setVendorModalOpen(true); }} className="text-blue-600">
                        <Edit2 className="w-4 h-4 inline" />
                      </button>
                      <button onClick={() => deleteVendor(v.id)} className="text-red-500">
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <VendorModal
        open={vendorModalOpen}
        onClose={() => setVendorModalOpen(false)}
        editing={editingVendor}
        onSaved={() => { fetchAll(); setVendorModalOpen(false); }}
      />
      <InvoiceModal
        open={invoiceModalOpen}
        onClose={() => setInvoiceModalOpen(false)}
        vendors={vendors}
        onSaved={() => { fetchAll(); setInvoiceModalOpen(false); }}
      />
      <PayInvoiceModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        invoice={payInvoice}
        onSaved={() => { fetchAll(); setPaymentModalOpen(false); }}
      />
    </div>
  );
}

function VendorModal({ open, onClose, editing, onSaved }) {
  const [form, setForm] = useState({ name: '', vat_payer: false, contact_phone: '', note: '' });
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (editing) setForm({
      name: editing.name || '', vat_payer: !!editing.vat_payer,
      contact_phone: editing.contact_phone || '', note: editing.note || '',
    });
    else setForm({ name: '', vat_payer: false, contact_phone: '', note: '' });
  }, [editing, open]);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Ad tələb olunur'); return; }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editing) await axios.put(`${API}/vendors/${editing.id}`, form, { headers });
      else await axios.post(`${API}/vendors`, form, { headers });
      toast.success(editing ? 'Yeniləndi' : 'Əlavə edildi');
      onSaved();
    } catch (e) { toast.error('Xəta: ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? 'Tədarükçünü redaktə et' : 'Yeni tədarükçü'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm">Ad</label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="vendor-name-input" />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="vat-payer"
              checked={form.vat_payer}
              onChange={e => setForm({ ...form, vat_payer: e.target.checked })}
              data-testid="vendor-vat-checkbox"
            />
            <label htmlFor="vat-payer" className="text-sm font-medium">
              ƏDV ödəyicisidir (18% qaimələrə avtomatik tətbiq edilir)
            </label>
          </div>
          <div>
            <label className="text-sm">Telefon</label>
            <Input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} />
          </div>
          <div>
            <label className="text-sm">Qeyd</label>
            <Input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
          </div>
          <p className="text-xs text-gray-500">⚠️ ƏDV statusu dəyişildikdə yalnız gələcək qaimələrə təsir edir (köhnələr eyni qalır).</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Ləğv</Button>
          <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="vendor-save-btn">Yadda saxla</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceModal({ open, onClose, vendors, onSaved }) {
  const [form, setForm] = useState({
    vendor_id: '',
    invoice_number: '',
    date: new Date().toISOString().slice(0, 10),
    due_date: '',
    base_amount: '',
    note: '',
  });
  const [config, setConfig] = useState({ vat_rate: 18 });
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (open) {
      setForm({ vendor_id: '', invoice_number: '', date: new Date().toISOString().slice(0, 10), due_date: '', base_amount: '', note: '' });
      axios.get(`${API}/config`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setConfig(r.data));
    }
  }, [open]);

  const selectedVendor = vendors.find(v => v.id === form.vendor_id);
  const isVat = !!selectedVendor?.vat_payer;
  const base = parseFloat(form.base_amount) || 0;
  const vatAmount = isVat ? base * config.vat_rate / 100 : 0;
  const total = base + vatAmount;

  const save = async () => {
    if (!form.vendor_id) { toast.error('Tədarükçü seçin'); return; }
    if (!form.invoice_number.trim()) { toast.error('Qaimə № tələb olunur'); return; }
    if (base <= 0) { toast.error('Əsas məbləğ müsbət olmalıdır'); return; }
    try {
      await axios.post(`${API}/invoices`, {
        vendor_id: form.vendor_id,
        invoice_number: form.invoice_number.trim(),
        date: form.date,
        due_date: form.due_date,
        base_amount: base,
        note: form.note,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Qaimə əlavə edildi');
      onSaved();
    } catch (e) { toast.error('Xəta: ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Yeni qaimə</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm">Tədarükçü</label>
            <Select value={form.vendor_id} onValueChange={v => setForm({ ...form, vendor_id: v })}>
              <SelectTrigger data-testid="invoice-vendor-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent>
                {vendors.map(v => (
                  <SelectItem key={v.id} value={v.id}>{v.name} {v.vat_payer ? '(ƏDV)' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm">Qaimə №</label>
              <Input value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} data-testid="invoice-number-input" />
            </div>
            <div>
              <label className="text-sm">Tarix</label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-sm">Əsas məbləğ (ƏDV-siz) ₼</label>
            <Input type="number" step="0.01" value={form.base_amount} onChange={e => setForm({ ...form, base_amount: e.target.value })} data-testid="invoice-base-input" />
          </div>
          <div>
            <label className="text-sm">Ödəniş son tarixi (istəyə bağlı)</label>
            <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
          </div>

          {selectedVendor && (
            <div className={`rounded-lg p-3 text-sm space-y-1 ${isVat ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50 border'}`}>
              <div className="flex justify-between"><span>Əsas:</span><span className="font-mono">{fmt(base)}</span></div>
              <div className="flex justify-between">
                <span>{isVat ? `ƏDV (${config.vat_rate}%):` : 'ƏDV tətbiq edilmir'}</span>
                <span className="font-mono">{fmt(vatAmount)}</span>
              </div>
              <div className="flex justify-between border-t pt-1 font-bold"><span>CƏM:</span><span className="font-mono">{fmt(total)}</span></div>
            </div>
          )}

          <div>
            <label className="text-sm">Qeyd</label>
            <Input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Ləğv</Button>
          <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="invoice-save-btn">Yadda saxla</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayInvoiceModal({ open, onClose, invoice, onSaved }) {
  const [form, setForm] = useState({ amount: '', date: new Date().toISOString().slice(0, 10), payment_method: 'transfer', reference: '' });
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (open && invoice) {
      setForm({ amount: String(invoice.remaining), date: new Date().toISOString().slice(0, 10), payment_method: 'transfer', reference: '' });
    }
  }, [open, invoice]);

  if (!invoice) return null;

  const save = async () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { toast.error('Məbləğ müsbət olmalıdır'); return; }
    if (amount > invoice.remaining + 0.01) { toast.error(`Qalıq ${invoice.remaining}-dan çoxdur`); return; }
    try {
      await axios.post(`${API}/invoice-payments`, {
        invoice_id: invoice.id, amount, date: form.date,
        payment_method: form.payment_method, reference: form.reference,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Ödəniş qeydə alındı');
      onSaved();
    } catch (e) { toast.error('Xəta: ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{invoice.vendor_name} — {invoice.invoice_number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-gray-50 p-3 rounded text-sm space-y-1">
            <div className="flex justify-between"><span>Cəm məbləğ:</span><span className="font-mono">{fmt(invoice.total)}</span></div>
            <div className="flex justify-between"><span>Ödənilib:</span><span className="font-mono text-emerald-700">{fmt(invoice.paid_so_far)}</span></div>
            <div className="flex justify-between font-bold border-t pt-1"><span>Qalıq:</span><span className="font-mono text-red-700">{fmt(invoice.remaining)}</span></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm">Ödəniş məbləği ₼</label>
              <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="invpay-amount-input" />
            </div>
            <div>
              <label className="text-sm">Tarix</label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-sm">Üsul</label>
            <Select value={form.payment_method} onValueChange={v => setForm({ ...form, payment_method: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="transfer">Köçürmə</SelectItem>
                <SelectItem value="cash">Nağd</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm">Referans</label>
            <Input value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Ləğv</Button>
          <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="invpay-save-btn">Ödə</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
